import { useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as wishlistItemsService from "../services/wishlistItemsService";
import * as claimedItemsService from "../services/claimedItemsService";

export function useWishlistItemsQuery(nameId, isOwner) {
  const queryClient = useQueryClient();
  // A reorder in flight has already applied its new order optimistically via
  // setQueryData; an incoming broadcast from someone else's unrelated change
  // (e.g. a claim) would otherwise invalidate mid-write and clobber it with
  // the pre-write order. WishlistItems.js sets this while a drag's writes
  // are in flight.
  const reorderPendingRef = useRef(false);

  useEffect(() => {
    if (!nameId) return undefined;
    const unsubscribe = wishlistItemsService.subscribeToItemUpdates(nameId, () => {
      if (reorderPendingRef.current) return;
      queryClient.invalidateQueries({ queryKey: ["wishlistItems", nameId] });
    });
    return unsubscribe;
  }, [nameId, queryClient]);

  const query = useQuery({
    queryKey: ["wishlistItems", nameId],
    queryFn: () => wishlistItemsService.fetchItems(nameId, isOwner),
    enabled: !!nameId,
  });

  return { ...query, reorderPendingRef };
}

function useItemsMutation(nameId, mutationFn) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["wishlistItems", nameId] });
      wishlistItemsService.notifyItemsUpdated(nameId);
    },
  });
}

export function useAddItem(nameId) {
  return useItemsMutation(nameId, wishlistItemsService.addItem);
}

export function useUpdateItem(nameId) {
  return useItemsMutation(nameId, wishlistItemsService.updateItem);
}

export function useDeleteItem(nameId) {
  return useItemsMutation(nameId, wishlistItemsService.deleteItem);
}

export function useClaimItem(nameId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: wishlistItemsService.claimItem,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["wishlistItems", nameId] });
      wishlistItemsService.notifyItemsUpdated(nameId);
      queryClient.invalidateQueries({ queryKey: ["claimedItems", variables.userId] });
      claimedItemsService.notifyClaimedUpdated(variables.userId);
    },
  });
}

export function useUnclaimItem(nameId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: claimedItemsService.unclaimItem,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["wishlistItems", nameId] });
      wishlistItemsService.notifyItemsUpdated(nameId);
      queryClient.invalidateQueries({ queryKey: ["claimedItems", variables.userId] });
      claimedItemsService.notifyClaimedUpdated(variables.userId);
    },
  });
}

export function useBulkImportItems(nameId) {
  return useItemsMutation(nameId, (items) => wishlistItemsService.bulkImportItems(nameId, items));
}

export function useReorderItems(nameId) {
  return useItemsMutation(nameId, wishlistItemsService.reorderItems);
}
